import cn from 'classnames'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import styles from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption.style'
import Pill, { PillProps } from 'ext-ui/components/Pill/Pill'
import { TextInputVariant } from 'ext-ui/components/TextInput/TextInput'
import { blue400 } from 'ext-ui/style/colors'
import React, { ComponentPropsWithRef } from 'react'
import SvgCheckFilled from 'src/components/Svgs/SvgCheckFilled'
import { useTranslate } from 'src/localization/Trns'

export interface DropdownOptionModel<T = unknown> {
  value: string
  label?: string
  details?: T
  sublabel?: string
  pill?: string | PillProps
  icon?: React.ReactElement
  bold?: boolean
  disabled?: boolean
  selected?: boolean
  variant?: TextInputVariant
}

type DropdownOptionProps<T> = DropdownOptionModel<T> &
  ComponentPropsWithRef<'li'> & {
    multi?: boolean
    highlighted?: boolean
    /**
     * When true (single-select only), the reserved leading checkmark column is
     * omitted so the option isn't indented by an otherwise-empty column.
     */
    hideSelectedIcon?: boolean
  }
const DropdownOptionInner = <T,>(
  props: DropdownOptionProps<T>,
  ref: React.Ref<HTMLLIElement>,
) => {
  const {
    value,
    label,
    sublabel,
    icon,
    pill,
    bold = true,
    disabled,
    selected = false,
    variant = 'medium',
    highlighted = false,
    multi,
    hideSelectedIcon = false,
    className,
    ...rest
  } = props
  const translate = useTranslate()

  return (
    <li
      ref={ref}
      role="option"
      tabIndex={0}
      aria-selected={selected}
      aria-disabled={disabled}
      {...rest}
      className={cn(className, variant, { disabled, selected, highlighted })}
      css={styles}
    >
      {!multi && !hideSelectedIcon && (
        <div className="selected-icon">
          {selected && <SvgCheckFilled fill={blue400} />}
        </div>
      )}
      {multi && (
        <Checkbox
          aria-label={translate('Dropdown Option Checkbox')}
          className={'checkbox'}
          checked={selected}
        />
      )}
      {icon}
      <div className="option-label-wrapper">
        <div className="flex-row gap-2 align-center">
          <span className={cn('ellipsis', { bold, secondary: value === '' })}>
            {label ?? value}
          </span>
          {pill &&
            (typeof pill === 'string' ? (
              <Pill size={'small'}>{pill}</Pill>
            ) : (
              <Pill {...pill} />
            ))}
        </div>
        {sublabel && <span className="option-sublabel">{sublabel}</span>}
      </div>
    </li>
  )
}

const DropdownOption = React.forwardRef(DropdownOptionInner) as <T = unknown>(
  props: DropdownOptionProps<T> & {
    ref?: React.Ref<HTMLLIElement | null>
  },
) => React.ReactElement

export default DropdownOption
